import React from 'react';
import { Row, Col, Button } from 'react-bootstrap';
import { CSSTransitionGroup } from 'react-transition-group';
import fuzzyFilterFactory from 'react-fuzzy-filter';
import { formatDate } from '../../helpers';
import PaginatedList from '../../components/PaginatedList';
import ListFilterBox from '../../components/ListFilterBox';
import SelectDropdown from '../../components/SelectDropdown';

const ControlHeader = props => (
  <Row className="tools">
    <Col sm={4} className="table-header">
      <ListFilterBox
        onChange={props.filterBoxChange}
        fuzzyFilterFactory={props.fuzzyFilterFactory}
      />
    </Col>

    <Col sm={5} className="table-header" />
    <Col sm={3} className="table-header">
      <Button
        bsStyle="primary"
        type="submit"
        className="relative-padding"
        onClick={props.addButtonClick}
      >
        Add Organisation
      </Button>
    </Col>
  </Row>
);

const TableHeader = props => (
  <div>
    <Row>
      <Col sm={4} className="table-header">
        Organisation name
      </Col>
      <Col sm={3} className="table-header">
        Date created
      </Col>
      <Col sm={3} className="table-header">
        Date updated
      </Col>
      <Col sm={2} className="table-header">
        Action
      </Col>
    </Row>
    <hr />
  </div>
);

const AddNewRow = props => (
  <Row>
    <Col sm={4} className="table-entry">
      <SelectDropdown
        onChange={props.selectOnChange}
        value={props.selectValue}
        options={props.items}
        aria-label="Organisation Select Box"
        focus={true}
      />
    </Col>
    <Col sm={2} className="table-entry">
      <Button
        className="relative-padding"
        bsStyle="default"
        onClick={props.addItem}
      >
        Add
      </Button>
    </Col>
    <Col sm={2} className="table-entry">
      <Button
        bsStyle="link"
        className="relative-padding"
        onClick={props.cancel}
      >
        Cancel
      </Button>
    </Col>
  </Row>
);

const LineItem = props => {
  const organisation = props.item;
  return (
    <Row key={organisation.id}>
      <Col sm={4} className="table-entry">
        {organisation.name}
      </Col>
      <Col sm={3} className="table-entry">
        {formatDate(organisation.dateCreated, true)}
      </Col>
      <Col sm={3} className="table-entry">
        {formatDate(organisation.dateUpdated, true)}
      </Col>
      <Col sm={2} className="table-entry">
        {!organisation.ownsShapeSet && (
          <Button
            className="relative-padding action-button"
            bsStyle="link"
            onClick={() => props.removeItem(organisation)}
            aria-label={`Remove ${organisation.name}`}
          >
            Remove
          </Button>
        )}
      </Col>
    </Row>
  );
};

class ShapeSetOrganisations extends React.Component {
  static displayName = 'ShapeSetOrganisations';

  constructor(props) {
    super(props);
    this.state = {
      newOrgId: undefined,
      showNewOrg: false,
      filterValue: ''
    };
    this.fuzzyFilterFactory = fuzzyFilterFactory();
    this.timeoutId = undefined;
  }

  componentDidMount() {
    this.getOrganisations();

    this.screenReaderContent.focus();
  }

  componentWillUnmount() {
    // clear timeout to prevent setState on unmounted component
    clearTimeout(this.timeoutId);
  }

  getOrganisations() {
    const shapeSetId = parseInt(this.props.params.shapeSetId, 10);
    this.props.getShapeSetOrganisations(shapeSetId, true);
    this.props.getShapeSetOrganisations(shapeSetId, false);
  }

  removeOrganisation = organisation => {
    this.setState({
      isSaving: true,
      errorMsg: undefined,
      successMsg: undefined
    });

    const shapeSetId = parseInt(this.props.params.shapeSetId, 10);
    this.props
      .removeOrganisationFromShapeSet(shapeSetId, organisation)
      .then(response => this.success(response), error => this.failure(error));
  };

  addOrganisation = () => {
    if (this.state.newOrgId) {
      this.setState({
        isSaving: true,
        errorMsg: undefined,
        successMsg: undefined
      });

      const shapeSetId = parseInt(this.props.params.shapeSetId, 10);
      const newOrgId = this.state.newOrgId;
      this.props
        .addOrganisationToShapeSet(shapeSetId, newOrgId)
        .then(response => this.success(response), error => this.failure(error));
    }
    this.setState({ showNewOrg: false, newOrgId: undefined });
  };

  success = response => {
    this.setState({
      isSaving: false,
      errorMsg: undefined,
      successMsg: 'Success!'
    });
    this.timeoutId = setTimeout(
      () => this.setState({ successMsg: undefined }),
      3000
    );
  };

  failure = error => {
    this.setState({ isSaving: false, errorMsg: error.data.message });
  };

  addButtonClick = () => {
    this.setState({ showNewOrg: !this.state.showNewOrg });
  };

  updateFilterValue = filterValue => {
    this.setState({ filterValue: filterValue || '' });
  };

  selectOnChange = newOrgId => {
    this.setState({ newOrgId });
  };

  cancel = () => {
    this.setState({ showNewOrg: false, newOrgId: undefined });
  };

  render() {
    const { allocated, unallocated } = this.props.currentShapeSet.organisations;

    const LineItemWrapper = props => (
      <LineItem {...props} removeItem={this.removeOrganisation} />
    );

    return (
      <div>
        <Row />
        <h3
          className="screen-reader-only"
          tabIndex="-1"
          ref={i => (this.screenReaderContent = i)}
        >
          Showing organisations with access to catchment set{' '}
          {this.props.currentShapeSet.name}
        </h3>

        <ControlHeader
          filterBoxChange={this.updateFilterValue}
          fuzzyFilterFactory={this.fuzzyFilterFactory}
          addButtonClick={this.addButtonClick}
        />

        <TableHeader />

        {this.state.showNewOrg && (
          <AddNewRow
            items={unallocated}
            addItem={this.addOrganisation}
            selectValue={this.state.newOrgId}
            selectOnChange={this.selectOnChange}
            cancel={this.cancel}
          />
        )}

        <PaginatedList
          filterValue={this.state.filterValue}
          component={LineItemWrapper}
          items={allocated}
          fuzzyFilterFactory={this.fuzzyFilterFactory}
          pluralName="organisations"
          tableHeader={TableHeader}
        />
        <Row>
          <Col sm={12}>
            <CSSTransitionGroup
              transitionName="fade"
              transitionEnterTimeout={300}
              transitionLeaveTimeout={300}
            >
              {this.state.successMsg && (
                <p className="text-success">{this.state.successMsg}</p>
              )}
            </CSSTransitionGroup>
          </Col>
        </Row>
        {this.state.errorMsg && (
          <div className="alert alert-danger">{this.state.errorMsg}</div>
        )}
      </div>
    );
  }
}

export default ShapeSetOrganisations;



// WEBPACK FOOTER //
// ./src/containers/shapeset/ShapeSetOrganisations.js